import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type {
  AppData,
  DataFile,
  DataError,
  LoadProgress,
  Layout,
  RawPalette,
  RawPainterView,
} from './types';
import { fetchTextWithProgress } from './fetchWithProgress';
import { createValidators } from './validate';
import { checkLayoutSemantics } from './semantics';
import { toPaletteModel } from './palette';
import { toCameraPose } from './painterView';

export type DataUrls = Readonly<Record<DataFile, string>>;

export function parseAppData(
  texts: Readonly<Record<DataFile, string>>
): Result<AppData, DataError> {
  let layout: Layout;
  let rawPalette: RawPalette;
  let rawPainterView: RawPainterView;

  try {
    layout = JSON.parse(texts.layout) as Layout;
  } catch (e) {
    return err({
      file: 'layout',
      kind: 'parse',
      message: 'Failed to parse layout JSON',
      details: [e instanceof Error ? e.message : String(e)],
    });
  }

  try {
    rawPalette = JSON.parse(texts.palette) as RawPalette;
  } catch (e) {
    return err({
      file: 'palette',
      kind: 'parse',
      message: 'Failed to parse palette JSON',
      details: [e instanceof Error ? e.message : String(e)],
    });
  }

  try {
    rawPainterView = JSON.parse(texts.painterView) as RawPainterView;
  } catch (e) {
    return err({
      file: 'painterView',
      kind: 'parse',
      message: 'Failed to parse painterView JSON',
      details: [e instanceof Error ? e.message : String(e)],
    });
  }

  const validators = createValidators();

  const layoutResult = validators.layout(layout);
  if (!layoutResult.ok) return layoutResult;

  const paletteResult = validators.palette(rawPalette);
  if (!paletteResult.ok) return paletteResult;

  const painterViewResult = validators.painterView(rawPainterView);
  if (!painterViewResult.ok) return painterViewResult;

  const semanticErrors = checkLayoutSemantics(layoutResult.value);
  if (semanticErrors.length > 0) {
    return err({
      file: 'layout',
      kind: 'semantic',
      message: 'Layout semantic validation failed',
      details: semanticErrors,
    });
  }

  const paletteModelResult = toPaletteModel(paletteResult.value);
  if (!paletteModelResult.ok) return paletteModelResult;

  const cameraPoseResult = toCameraPose(painterViewResult.value);
  if (!cameraPoseResult.ok) return cameraPoseResult;

  return ok({
    layout: layoutResult.value,
    palette: paletteModelResult.value,
    painterView: cameraPoseResult.value,
  });
}

export async function loadAppData(
  urls: DataUrls,
  onProgress: (p: LoadProgress) => void,
  fetchImpl: typeof fetch = fetch
): Promise<Result<AppData, DataError>> {
  const files: DataFile[] = ['layout', 'palette', 'painterView'];
  const texts: Record<string, string> = {};

  for (const file of files) {
    const result = await fetchTextWithProgress(
      urls[file],
      (loaded, total) => {
        onProgress({ file, loadedBytes: loaded, totalBytes: total });
      },
      fetchImpl
    );

    if (!result.ok) {
      return err({
        file,
        kind: 'fetch',
        message: result.error,
        details: [],
      });
    }

    texts[file] = result.value;
  }

  return parseAppData(texts as Record<DataFile, string>);
}
